
import React, { useCallback } from "react";
import { Message } from "@/services/messages";
import { useConversation } from "@/hooks/messages/useConversation";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import ConversationHeader from "./ConversationHeader";
import ConversationBody from "./ConversationBody";
import MessageInput from "./MessageInput";
import { toast } from "sonner";

interface ConversationProps {
  messages: Message[];
  otherUserId: string;
  otherUsername: string;
  reagentId?: string;
  reagentName?: string;
  conversationTitle?: string;
  isLoading: boolean;
  hasMore: boolean;
  onBack: () => void;
  onMessageSent: () => void;
  onLoadMore: () => void;
  onRefresh: () => void;
  isRealtimeConnected?: boolean;
}

const Conversation: React.FC<ConversationProps> = ({
  messages,
  otherUserId,
  otherUsername,
  reagentId,
  reagentName,
  conversationTitle = "",
  isLoading,
  hasMore,
  onBack,
  onMessageSent,
  onLoadMore,
  onRefresh,
  isRealtimeConnected = false
}) => {
  // Use our custom hook for conversation logic
  const { 
    newMessage, 
    setNewMessage, 
    isSending, 
    optimisticMessages, 
    handleSendMessage 
  } = useConversation(messages, otherUserId, reagentId, onMessageSent);
  
  // Memoize refresh handler to prevent unnecessary re-renders
  const handleManualRefresh = useCallback(() => {
    onRefresh();
    toast.info("Refreshing conversation...");
  }, [onRefresh]);
  
  // Display loading spinner when loading initial messages
  if (isLoading && optimisticMessages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <LoadingSpinner size="md" />
        <p className="text-muted-foreground ml-2">Loading conversation...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <ConversationHeader
        conversationTitle={conversationTitle || otherUsername}
        reagentName={reagentName}
        onBack={onBack}
        onRefresh={handleManualRefresh}
        isLoading={isLoading}
        isRealtimeConnected={isRealtimeConnected}
      />
      
      <ConversationBody
        messages={optimisticMessages}
        otherUsername={otherUsername}
        isLoading={isLoading}
        hasMore={hasMore}
        onLoadMore={onLoadMore}
      />
      
      <div className="mt-auto">
        <MessageInput
          newMessage={newMessage}
          setNewMessage={setNewMessage}
          handleSendMessage={handleSendMessage}
          isSending={isSending}
        />
      </div>
    </div>
  );
};

export default React.memo(Conversation);
